import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, CircleUserRound, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../ui/LanguageSwitcher'
import { useSiteConfig } from '../../context/SiteConfigContext'
import { backofficeLoginUrl } from '../../config/urls'

const MAX_VISIBLE_NAV = 8

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const moreRef = useRef(null)
  const { t } = useTranslation()
  const { siteConfig } = useSiteConfig()
  const location = useLocation()

  // Cerrar dropdown "Más" al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(e) {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setIsMoreOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cerrar dropdown "Más" al cambiar de ruta
  useEffect(() => {
    setIsMoreOpen(false)
  }, [location.pathname])

  const allNavItems = [
    { key: 'home', name: t('header.nav.home'), href: '/' },
    { key: 'about', name: t('header.nav.about'), href: '/about' },
    { key: 'services', name: t('header.nav.services'), href: '/services' },
    { key: 'team', name: t('header.nav.team'), href: '/team' },
    { key: 'coverage', name: t('header.nav.coverage'), href: '/coverage' },
    { key: 'blog', name: t('header.nav.blog'), href: '/blog' },
    { key: 'forum', name: t('header.nav.forum'), href: '/forum' },
    { key: 'store', name: t('header.nav.store'), href: '/store' },
    { key: 'testimonials', name: t('header.nav.testimonials'), href: '/testimonials' },
    { key: 'faq', name: t('header.nav.faq'), href: '/faq' },
    { key: 'downloads', name: t('header.nav.downloads'), href: '/downloads' },
    { key: 'contact', name: t('header.nav.contact'), href: '/contact' },
  ]

  // Los toggles del Backoffice controlan visibilidad. La lógica clásica es
  // "muestra a menos que esté explícitamente false"; los items nuevos
  // (coverage, forum, faq) tienen default false en SiteConfigContext para que
  // no aparezcan hasta que el admin los active.
  const navCfg = siteConfig.navItems || {}
  const navOrder = Array.isArray(siteConfig.navOrder) ? siteConfig.navOrder : []

  // Ordenar según navOrder; items sin orden van al final
  const navigation = [...allNavItems]
    .filter(item => navCfg[item.key] !== false)
    .sort((a, b) => {
      const ia = navOrder.indexOf(a.key)
      const ib = navOrder.indexOf(b.key)
      if (ia === -1 && ib === -1) return 0
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })

  const visibleNav = navigation.slice(0, MAX_VISIBLE_NAV)
  const moreNav = navigation.slice(MAX_VISIBLE_NAV)

  // Login unificado: un solo botón que va al Backoffice con tema default.
  // El tema real se aplica después del login, según el perfil detectado del usuario.
  const loginHref = backofficeLoginUrl()

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <nav className="container-custom">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo-genika.png" alt="Génika - Salud a tu Domicilio" className="h-12 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {visibleNav.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="text-neutral-600 hover:text-primary-500 transition-colors duration-200 font-medium whitespace-nowrap"
              >
                {item.name}
              </Link>
            ))}

            {/* Dropdown "Más" — solo si hay items que no caben */}
            {moreNav.length > 0 && (
              <div className="relative" ref={moreRef}>
                <button
                  type="button"
                  onClick={() => setIsMoreOpen(prev => !prev)}
                  className="flex items-center gap-1 text-neutral-600 hover:text-primary-500 transition-colors duration-200 font-medium whitespace-nowrap"
                  aria-expanded={isMoreOpen}
                  aria-haspopup="true"
                >
                  <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isMoreOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMoreOpen && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-neutral-100 py-2 z-50">
                    {moreNav.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block px-4 py-2 text-sm text-neutral-600 hover:text-primary-500 hover:bg-primary-50 transition-colors duration-150"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            <LanguageSwitcher className="border-neutral-200 text-neutral-600 hover:border-primary-500 hover:text-primary-500" />

            {/* Iniciar sesión — botón único */}
            <a
              href={loginHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200"
            >
              <CircleUserRound className="h-5 w-5" />
              {t('header.myGenikaHealth')}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-neutral-600 hover:text-primary-500 hover:bg-gray-100"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="flex flex-col space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-neutral-600 hover:text-primary-500 transition-colors duration-200 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}

              {/* Mobile login */}
              <div className="border-t pt-4">
                <a
                  href={loginHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-200 w-fit"
                >
                  <CircleUserRound className="h-5 w-5" />
                  {t('header.myGenikaHealth')}
                </a>
              </div>

              <LanguageSwitcher className="border-neutral-200 text-neutral-600 hover:border-primary-500 hover:text-primary-500 w-fit" />
              <Link
                to="/contact"
                className="btn-primary text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('header.appointment')}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}

export default Header
